import { BetrokkenTrein } from "@/entities/betrokken-treinen";
import { RoleGate, UserRole } from "@/features/auth";
import {
    BetrokkenTreinMedewerkers,
    BetrokkenTreinMedewerkerToevoegen,
} from "@/features/betrokken-treinen/components/medewerkers/betrokken-trein-medewerkers.tsx";
import { Badge, Button, Collapsible, CollapsibleContent, CollapsibleTrigger, Icon } from "@/shared/components";
import { cn } from "@/shared/utils";
import { useState } from "react";
import EditBetrokkenTreinDialog from "./edit-betrokken-trein/edit-betrokken-trein-dialog";
import { BetrokkenTreinEvacuatieBus, BetrokkenTreinEvacuatieBusToevoegen } from "./evacuatie-bus/evacuatie-bus";
import { BetrokkenTreinEvacuatieTrein, BetrokkenTreinEvacuatieTreinToevoegen } from "./evacuatie-trein/evacuatie-trein";

type BetrokkenTreinenProps = {
    dossierId: string;
    trein: BetrokkenTrein;
    defaultOpen: boolean;
};

export function BetrokkenTreinItem({ dossierId, trein, defaultOpen }: BetrokkenTreinenProps) {
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [open, setOpen] = useState(defaultOpen);

    return (
        <li>
            <Collapsible open={open} onOpenChange={setOpen}>
                <CollapsibleTrigger className="flex w-full flex-col justify-between" asChild>
                    {/* use asChild here as workaround for the fact that nesting buttons is not allowed (until we decide on a better location for the edit button */}
                    <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setOpen(!open);
                            }
                        }}
                    >
                        <div className="border-default flex w-full items-center justify-between gap-2 border-t py-3">
                            <div className="flex items-center gap-4">
                                <h2 className="text-content-emphasis text-xl font-medium">{BetrokkenTrein.formatTrainLabel(trein)}</h2>

                                {trein.handelingsVrij != null && (
                                    <Badge variant={trein.handelingsVrij ? "green-subtle" : "red-subtle"} className="mr-0.5">
                                        {trein.handelingsVrij ? "Handelingsvrij" : "Niet handelingsvrij"}
                                    </Badge>
                                )}

                                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                                    <Button
                                        iconOnly
                                        icon="edit-box"
                                        variant="tertiary"
                                        onClick={(e) => {
                                            setEditDialogOpen(true);
                                            e.stopPropagation();
                                        }}
                                    />
                                </RoleGate>
                            </div>

                            <Icon name="chevron-down" size="m" className={cn("transition-transform", open && "rotate-180")} />
                        </div>
                    </div>
                </CollapsibleTrigger>
                <CollapsibleContent preventFocusClipping className="pt-0">
                    <div className="pb-4">
                        <div className="text-s text-content-subtle mb-2 flex w-full items-start gap-4 font-medium">
                            <span>{trein.typeLabel}</span>
                            {(trein.aantalReizigers || trein.aantalReizigers === 0) && <span>{trein.aantalReizigers} reizigers</span>}
                            {trein.materieel && (
                                <span>
                                    {trein.materieel.aanduiding} {trein.materieel.aantalBakken}
                                </span>
                            )}
                        </div>
                        <RoleGate
                            roles={[
                                UserRole.REGIEVOERDER,
                                UserRole.SUPPORTCONSULTANT,
                                UserRole.ALLEENLEZEN,
                                UserRole.DEWIT,
                                UserRole.TRANSVISION,
                            ]}
                        >
                            <div className="grid gap-y-4">
                                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                                    <BetrokkenTreinMedewerkers dossierId={dossierId} treinnummer={trein.treinNummer} />
                                </RoleGate>
                                <RoleGate roles={[UserRole.REGIEVOERDER, UserRole.DEWIT, UserRole.TRANSVISION]}>
                                    <BetrokkenTreinEvacuatieBus dossierId={dossierId} trein={trein} />
                                </RoleGate>
                                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                                    <BetrokkenTreinEvacuatieTrein key={`${!!trein.treinEvacuatie}`} dossierId={dossierId} trein={trein} />
                                </RoleGate>
                            </div>
                            <RoleGate roles={[UserRole.REGIEVOERDER]}>
                                <BetrokkenTreinItemActions dossierId={dossierId} trein={trein} />
                            </RoleGate>
                        </RoleGate>
                    </div>
                </CollapsibleContent>
            </Collapsible>
            {editDialogOpen && (
                <EditBetrokkenTreinDialog dossierNummer={dossierId} open={editDialogOpen} onOpenChange={setEditDialogOpen} trein={trein} />
            )}
        </li>
    );
}

type BetrokkenTreinItemActionsProps = {
    dossierId: string;
    trein: BetrokkenTrein;
};

function BetrokkenTreinItemActions({ dossierId, trein }: BetrokkenTreinItemActionsProps) {
    return (
        <div className="mt-4 inline-grid grid-cols-3 gap-x-4">
            <BetrokkenTreinMedewerkerToevoegen dossierId={dossierId} treinnummer={trein.treinNummer} />
            {!trein.busEvacuatie && <BetrokkenTreinEvacuatieBusToevoegen dossierId={dossierId} trein={trein} />}
            {!trein.treinEvacuatie && <BetrokkenTreinEvacuatieTreinToevoegen dossierId={dossierId} trein={trein} />}
        </div>
    );
}
